import './settings/settings-leaf.css';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { templateApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useLocalizedContent } from '../i18n/localized.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, EmptyState, Select, TextArea, TextInput } from './ui.tsx';
import { TaxonomyFields } from './taxonomy-fields.tsx';
import { emptyTemplateTags, type TemplateTags } from '@shared/constants/template-taxonomy.ts';
import { MAX_ROOM_QUANTITY } from '@shared/constants/index.ts';
import type { GroupTemplate, GroupTemplateRoom, RoomTemplate, ServiceTemplate } from '@shared/types/index.ts';

/**
 * Layouts: sets of rooms a quote can start from (a 4-bedroom house, then
 * trimmed to the job) or add as a repeated unit (40 apartments).
 *
 * Owners create and edit them here. Someone who may edit the library can
 * also save a quote's rooms as a new layout from the quote; that is a
 * new company setting, never an edit to one. Hiding a layout takes it off
 * new quotes; a quote that already copied it keeps its own rooms.
 */

interface DraftRoom {
  localId: string;
  source: GroupTemplateRoom;
}

interface Draft {
  name: string;
  notes: string;
  categoryCode: string;
  tags: TemplateTags;
  isFavorite: boolean;
  rooms: DraftRoom[];
}

function blankRoom(localId: string): DraftRoom {
  return {
    localId,
    source: {
      roomTemplateId: null,
      roomName: '',
      roomNameTranslations: null,
      quantity: 1,
      sizeCode: 'medium',
      conditionCode: 'average',
      flatPriceCents: null,
      measuredSquareFeet: null,
      floorType: null,
      customerNotes: null,
      services: [],
    },
  };
}

function draftFrom(template: GroupTemplate, nextId: () => string): Draft {
  const rooms = template.rooms.length > 0 ? template.rooms : [blankRoom(nextId()).source];
  return {
    name: template.name,
    notes: template.notes ?? '',
    categoryCode: template.categoryCode ?? '',
    tags: template.tags ?? emptyTemplateTags(),
    isFavorite: template.isFavorite,
    rooms: rooms.map((source) => ({ localId: nextId(), source })),
  };
}

function servicesFor(template: RoomTemplate, library: readonly ServiceTemplate[]): GroupTemplateRoom['services'] {
  const byId = new Map(library.map((service) => [service.id, service]));
  return template.services.flatMap((link) => {
    const service = byId.get(link.serviceTemplateId);
    if (!service) return [];
    return [
      {
        serviceTemplateId: service.id,
        name: service.name,
        description: service.customerDescription,
        nameTranslations: service.nameTranslations,
        descriptionTranslations: service.customerDescriptionTranslations,
        estimatedMinutes: link.defaultMinutesOverride ?? service.defaultMinutes,
        isSelected: link.defaultSelected,
        showOnCustomerQuote: service.showOnCustomerQuote,
        isTaxable: service.isTaxable,
        frequency: 'every_visit' as const,
        measurementBasis: service.measurementBasis,
        measuredQuantity: link.defaultMeasuredQuantity ?? null,
        productionRatePerHour: service.measurementBasis === 'time' ? null : service.productionRatePerHour,
      },
    ];
  });
}

function roomFromLibrary(
  template: RoomTemplate,
  library: readonly ServiceTemplate[],
  quantity: number,
): GroupTemplateRoom {
  return {
    roomTemplateId: template.id,
    roomName: template.name,
    roomNameTranslations: template.nameTranslations,
    quantity,
    sizeCode: 'medium',
    conditionCode: 'average',
    flatPriceCents: template.defaultFlatPriceCents,
    measuredSquareFeet: template.defaultSquareFeet ?? null,
    floorType: template.measurementBasis === 'time' ? null : template.defaultFloorType,
    customerNotes: null,
    services: servicesFor(template, library),
  };
}

function payloadRooms(rooms: readonly DraftRoom[]): GroupTemplateRoom[] {
  return rooms.map((room) => ({
    ...room.source,
    roomName: room.source.roomName.trim(),
    quantity: Math.min(MAX_ROOM_QUANTITY, Math.max(1, Math.trunc(room.source.quantity) || 1)),
  }));
}

export default function GroupTemplatesSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const localized = useLocalizedContent();
  const writable = can('templates:write');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const idCounter = useRef(0);
  const nextId = () => {
    idCounter.current += 1;
    return `room-${idCounter.current}`;
  };

  const groups = useQuery({
    queryKey: ['group-templates', 'with-hidden'],
    queryFn: () => templateApi.groups(true),
  });
  const roomLibrary = useQuery({
    queryKey: ['room-templates'],
    queryFn: () => templateApi.rooms(),
    enabled: draft !== null,
  });
  const taskLibrary = useQuery({
    queryKey: ['service-templates'],
    queryFn: () => templateApi.services(),
    enabled: draft !== null,
  });

  function refresh() {
    return queryClient.invalidateQueries({ queryKey: ['group-templates'] });
  }

  const hide = useMutation({
    mutationFn: (id: string) => templateApi.deactivateGroup(id),
    onSuccess: async () => {
      await refresh();
    },
  });

  const restore = useMutation({
    mutationFn: (id: string) => templateApi.restoreGroup(id),
    onSuccess: async () => {
      await refresh();
    },
  });

  const favorite = useMutation({
    mutationFn: (template: { id: string; isFavorite: boolean }) =>
      templateApi.setGroupFavorite(template.id, !template.isFavorite),
    onSuccess: async () => {
      await refresh();
    },
  });

  const importStarter = useMutation({
    mutationFn: () => templateApi.importStarter(),
    onSuccess: async (added) => {
      setError(null);
      const total = added.services + added.rooms + added.groups;
      setMessage(
        total === 0
          ? t('groupTemplates.importNone')
          : t('groupTemplates.importDone', {
              services: added.services,
              rooms: added.rooms,
              groups: added.groups,
            }),
      );
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['room-templates'] });
      await queryClient.invalidateQueries({ queryKey: ['service-templates'] });
    },
    onError: (caught) => {
      setMessage(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('groupTemplates.importError'));
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('Nothing to save');
      const name = draft.name.trim();
      const rooms = payloadRooms(draft.rooms);
      if (!name) throw new Error(t('groupTemplates.needsName'));
      if (rooms.length === 0 || rooms.some((room) => room.roomName.length === 0)) {
        throw new Error(t('groupTemplates.needsRoom'));
      }
      const tags =
        draft.tags.segments.length + draft.tags.jobTypes.length + draft.tags.floorTypes.length === 0
          ? null
          : draft.tags;
      // The stored name is the source-language one, which is what the field
      // was filled with. Omitting it on an unchanged save keeps the other
      // language; the server also keeps translations when the text matches.
      const cached = queryClient.getQueryData<{ items: GroupTemplate[] }>(['group-templates', 'with-hidden']);
      const stored = editingId ? cached?.items.find((item) => item.id === editingId) : undefined;
      const body = {
        ...(stored && name === stored.name ? {} : { name }),
        notes: draft.notes.trim() || null,
        categoryCode: draft.categoryCode || null,
        tags,
        isFavorite: draft.isFavorite,
        rooms,
      };
      if (editingId) return templateApi.updateGroup(editingId, body);
      // The language the name was typed in, which is the one on screen.
      // `company.language` is the document default and can disagree with it;
      // filing a Spanish name as English is how the other language goes missing.
      return templateApi.createGroup({
        ...body,
        textLanguage: localized.language,
      });
    },
    onSuccess: async () => {
      setError(null);
      setMessage(t('groupTemplates.created'));
      setEditingId(null);
      setDraft(null);
      await refresh();
    },
    onError: (caught) => {
      setMessage(null);
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : t('groupTemplates.saveError'),
      );
    },
  });

  function beginCreate() {
    setError(null);
    setEditingId(null);
    setDraft({
      name: '',
      notes: '',
      categoryCode: '',
      tags: emptyTemplateTags(),
      isFavorite: false,
      rooms: [blankRoom(nextId())],
    });
  }

  function beginEdit(template: GroupTemplate) {
    setError(null);
    setEditingId(template.id);
    setDraft(draftFrom(template, nextId));
  }

  function patchRoom(localId: string, source: GroupTemplateRoom) {
    setDraft((current) =>
      current
        ? {
            ...current,
            rooms: current.rooms.map((room) => (room.localId === localId ? { ...room, source } : room)),
          }
        : current,
    );
  }

  function chooseLibraryRoom(localId: string, templateId: string) {
    const current = draft?.rooms.find((room) => room.localId === localId);
    if (!current) return;
    // An empty task list is a real snapshot: saving it prices the room at
    // nothing. Wait until the library has loaded, and do not substitute [].
    if (!taskLibrary.isSuccess || !taskLibrary.data) return;
    if (!templateId) {
      patchRoom(localId, {
        ...current.source,
        roomTemplateId: null,
        roomKey: null,
        roomNameTranslations: null,
        services: [],
      });
      return;
    }
    const template = roomLibrary.data?.items.find((item) => item.id === templateId);
    if (!template) return;
    patchRoom(localId, roomFromLibrary(template, taskLibrary.data.items, current.source.quantity));
  }

  const all = groups.data?.items ?? [];
  const items = showHidden ? all : all.filter((template) => template.isActive);
  const tasksReady = taskLibrary.isSuccess;
  const roomOptions = [
    { value: '', label: t('groupTemplates.customRoom') },
    ...(roomLibrary.data?.items ?? []).map((template) => ({
      value: template.id,
      label: localized.text(template.name, template.nameTranslations),
    })),
  ];

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="group-library-heading">
      <div className="work-section-heading">
        <h2 id="group-library-heading" className="text-lg font-semibold">
          {t('groupTemplates.heading')}
        </h2>
        {writable ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={beginCreate}>
              {t('groupTemplates.newLayout')}
            </Button>
            <Button onClick={() => importStarter.mutate()} disabled={importStarter.isPending}>
              {t('groupTemplates.importStarter')}
            </Button>
          </div>
        ) : null}
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('groupTemplates.description')}</p>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('groupTemplates.importHint')}</p>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('libraryTaxonomy.estimates')}</p>

      {message ? (
        <div className="mb-4">
          <Alert tone="success">{message}</Alert>
        </div>
      ) : null}
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {draft && taskLibrary.isError ? (
        <div className="mb-4">
          <Alert tone="error">{t('groupTemplates.tasksError')}</Alert>
        </div>
      ) : null}

      {draft ? (
        <form
          className="mb-6 rounded-md border border-[color:var(--color-line)] p-3"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <TextInput
            label={t('groupTemplates.nameLabel')}
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            required
          />
          <TextArea
            label={t('groupTemplates.notesLabel')}
            hint={t('groupTemplates.notesHint')}
            rows={2}
            value={draft.notes}
            onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
          />
          <h3 className="mb-2 text-sm font-semibold">{t('groupTemplates.roomsHeading')}</h3>
          <ul className="space-y-3">
            {draft.rooms.map((room) => (
              <li key={room.localId} className="rounded-md border border-[color:var(--color-line)] p-3">
                <div className="grid gap-x-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
                  <Select
                    label={t('groupTemplates.roomLabel')}
                    hint={tasksReady || taskLibrary.isError ? undefined : t('groupTemplates.tasksLoading')}
                    disabled={!tasksReady}
                    value={room.source.roomTemplateId ?? ''}
                    onChange={(event) => chooseLibraryRoom(room.localId, event.target.value)}
                    options={
                      room.source.roomTemplateId &&
                      !roomOptions.some((option) => option.value === room.source.roomTemplateId)
                        ? [...roomOptions, { value: room.source.roomTemplateId, label: room.source.roomName }]
                        : roomOptions
                    }
                  />
                  <TextInput
                    label={t('groupTemplates.howMany')}
                    type="number"
                    min={1}
                    max={MAX_ROOM_QUANTITY}
                    value={String(room.source.quantity)}
                    onChange={(event) => {
                      const quantity = Number(event.target.value);
                      patchRoom(room.localId, {
                        ...room.source,
                        quantity: Number.isFinite(quantity) ? quantity : room.source.quantity,
                      });
                    }}
                  />
                </div>
                {room.source.roomTemplateId ? null : (
                  <TextInput
                    label={t('groupTemplates.roomNameLabel')}
                    value={room.source.roomName}
                    onChange={(event) => patchRoom(room.localId, { ...room.source, roomName: event.target.value })}
                  />
                )}
                {draft.rooms.length > 1 ? (
                  <Button
                    type="button"
                    onClick={() =>
                      setDraft({ ...draft, rooms: draft.rooms.filter((item) => item.localId !== room.localId) })
                    }
                  >
                    {t('groupTemplates.removeRoom')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="mb-4">
            <Button type="button" onClick={() => setDraft({ ...draft, rooms: [...draft.rooms, blankRoom(nextId())] })}>
              {t('groupTemplates.addRoom')}
            </Button>
          </div>
          <details className="mb-4">
            <summary className="min-h-11 cursor-pointer text-sm font-semibold">{t('quoteWork.more')}</summary>
            <TaxonomyFields
              kind="room"
              categoryCode={draft.categoryCode}
              tags={draft.tags}
              isFavorite={draft.isFavorite}
              onChange={(patch) => setDraft((current) => (current ? { ...current, ...patch } : current))}
            />
          </details>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" type="submit" disabled={save.isPending || !tasksReady}>
              {editingId ? t('groupTemplates.save') : t('groupTemplates.create')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft(null);
                setEditingId(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      <Checkbox
        label={t('groupTemplates.showHidden')}
        checked={showHidden}
        onChange={(event) => setShowHidden(event.target.checked)}
      />

      {groups.isLoading ? null : items.length === 0 ? (
        <EmptyState title={t('groupTemplates.emptyTitle')}>
          <p>{t('groupTemplates.emptyMessage')}</p>
        </EmptyState>
      ) : (
        <ul className="divide-y divide-[color:var(--color-line)]">
          {items.map((template) => (
            <li key={template.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="font-medium">
                  {template.isFavorite ? <span aria-hidden="true">★ </span> : null}
                  {localized.text(template.name, template.nameTranslations)}
                  {!template.isActive ? (
                    <span className="ml-2 badge border-[color:var(--color-muted)] text-[color:var(--color-muted)]">
                      {t('groupTemplates.hidden')}
                    </span>
                  ) : null}
                </p>
                <p className="text-sm text-[color:var(--color-muted)]">
                  {t('groupTemplates.roomCount', { count: template.rooms.length })}
                  {template.categoryCode
                    ? ` · ${t(`libraryTaxonomy.room.${template.categoryCode}`, { defaultValue: template.categoryCode })}`
                    : ''}
                </p>
              </div>
              {writable && editingId !== template.id ? (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => beginEdit(template)}>{t('groupTemplates.edit')}</Button>
                  <Button disabled={favorite.isPending} onClick={() => favorite.mutate(template)}>
                    {template.isFavorite ? t('groupTemplates.unfavorite') : t('groupTemplates.favorite')}
                  </Button>
                  {template.isActive ? (
                    <Button variant="danger" disabled={hide.isPending} onClick={() => hide.mutate(template.id)}>
                      {t('groupTemplates.delete')}
                    </Button>
                  ) : (
                    <Button disabled={restore.isPending} onClick={() => restore.mutate(template.id)}>
                      {t('groupTemplates.restore')}
                    </Button>
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {writable ? <p className="field-hint mt-4">{t('groupTemplates.deleteNote')}</p> : null}
    </section>
  );
}
